// src/components/ui/index.js
import { buildTimezoneGroups } from "../../utils/timezones";
export { default as PhoneField } from "./PhoneField";
export { default as ServiceVisual } from "./ServiceVisual";

// ── TimezoneSelect ───────────────────────────────────────────────────────────
// Shared by Onboarding and the Settings tab. `detectedZone` (optional) is
// injected as an extra option if it isn't already in the curated list, so an
// unusual browser-detected timezone is never silently lost or mismatched.
export function TimezoneSelect({ value, onChange, detectedZone, className = "form-input" }) {
  const groups = buildTimezoneGroups(detectedZone);
  return (
    <select className={className} value={value} onChange={(e) => onChange(e.target.value)}>
      {groups.map((g) => (
        <optgroup key={g.label} label={g.label}>
          {g.options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

// ── StepBar ──────────────────────────────────────────────────────────────────
export function StepBar({ current, steps }) {
  return (
    <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-1">
      {steps.map((label, i) => {
        const num    = i + 1;
        const active = current === num;
        const done   = current > num;
        return (
          <div key={label} className="flex items-center gap-1 shrink-0">
            <div className="flex items-center gap-2">
              <div className={`step-dot ${active ? "step-dot-active" : done ? "step-dot-done" : ""}`}>
                {done ? "✓" : num}
              </div>
              <span className={`text-xs font-medium hidden sm:block transition-colors
                ${active ? "text-brand-600" : done ? "text-ink-400" : "text-ink-300"}`}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`w-6 sm:w-10 h-px mx-2 transition-colors
                ${done ? "bg-brand-300" : "bg-surface-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── SectionHeader ─────────────────────────────────────────────────────────────
export function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-6">
      <h2 className="font-semibold text-2xl md:text-3xl text-ink-900">{title}</h2>
      {subtitle && <p className="text-ink-400 text-sm mt-1 font-sans">{subtitle}</p>}
    </div>
  );
}

// ── CheckBadge ────────────────────────────────────────────────────────────────
export function CheckBadge({ className = "" }) {
  return (
    <div className={`w-6 h-6 rounded-full bg-brand-500 flex items-center justify-center
                     text-white text-xs font-bold shrink-0 shadow-brand ${className}`}>
      ✓
    </div>
  );
}

// ── StarRating ────────────────────────────────────────────────────────────────
export function StarRating({ rating, size = "sm" }) {
  const full  = Math.floor(rating);
  const sizes = { sm: "text-xs", md: "text-sm" };
  return (
    <span className={`${sizes[size]} text-amber-400`}>
      {"★".repeat(full)}{"☆".repeat(5 - full)}{" "}
      <span className="text-ink-400 text-xs">{rating}</span>
    </span>
  );
}

// ── ConfirmRow ────────────────────────────────────────────────────────────────
export function ConfirmRow({ label, value, highlight = false }) {
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-surface-100 last:border-0">
      <span className="text-ink-400 text-sm font-sans">{label}</span>
      <span className={`text-sm font-sans font-medium
        ${highlight ? "text-brand-600 font-semibold text-base" : "text-ink-800"}`}>
        {value}
      </span>
    </div>
  );
}

// ── FormField ─────────────────────────────────────────────────────────────────
export function FormField({ label, required, children }) {
  return (
    <div>
      <label className="form-label">
        {label}{required && <span className="text-brand-500 ml-0.5 normal-case tracking-normal">*</span>}
      </label>
      {children}
    </div>
  );
}

// ── LoadingSpinner ────────────────────────────────────────────────────────────
export function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="w-8 h-8 border-2 border-surface-200 border-t-brand-500 rounded-full animate-spin" />
    </div>
  );
}

// ── Skeleton primitives ───────────────────────────────────────────────────────
export function Skeleton({ className = "" }) {
  return (
    <div className={`animate-pulse bg-surface-200 rounded-lg ${className}`} />
  );
}

// ── Skeleton: public booking page (hero + tabs + service cards) ───────────────
export function TenantPageSkeleton() {
  return (
    <div className="min-h-screen bg-surface-50 animate-pulse">
      {/* Hero */}
      <div className="relative h-52 sm:h-72 bg-surface-200 w-full" />
      {/* Logo + name */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-10 relative z-10">
        <div className="flex items-end gap-4 mb-6">
          <div className="w-20 h-20 rounded-2xl bg-surface-300 border-4 border-white shadow shrink-0" />
          <div className="pb-1 space-y-2">
            <div className="h-6 w-40 bg-surface-300 rounded-lg" />
            <div className="h-3.5 w-28 bg-surface-200 rounded-lg" />
          </div>
        </div>
        {/* Tabs */}
        <div className="flex gap-4 border-b border-surface-200 pb-px mb-8">
          {[80, 60, 72, 64].map((w, i) => (
            <div key={i} className={`h-3.5 bg-surface-200 rounded-lg`} style={{ width: w }} />
          ))}
        </div>
        {/* Service cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-2xl border border-surface-100 p-5 space-y-3">
              <div className="flex justify-between">
                <div className="h-4 w-28 bg-surface-200 rounded-lg" />
                <div className="h-4 w-12 bg-surface-200 rounded-lg" />
              </div>
              <div className="h-3 w-36 bg-surface-100 rounded-lg" />
              <div className="h-3 w-20 bg-surface-100 rounded-lg" />
              <div className="h-9 w-full bg-surface-200 rounded-xl mt-2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Skeleton: service step (booking flow step 1) ──────────────────────────────
export function ServiceStepSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-6 w-32 bg-surface-200 rounded-lg mb-1" />
      <div className="h-3.5 w-56 bg-surface-100 rounded-lg mb-6" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-surface-100 p-5 space-y-3">
            <div className="flex justify-between">
              <div className="h-4 w-28 bg-surface-200 rounded-lg" />
              <div className="h-4 w-12 bg-surface-200 rounded-lg" />
            </div>
            <div className="h-3 w-36 bg-surface-100 rounded-lg" />
            <div className="h-9 w-full bg-surface-200 rounded-xl mt-2" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Skeleton: barber/team step ────────────────────────────────────────────────
export function BarberStepSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-6 w-36 bg-surface-200 rounded-lg mb-1" />
      <div className="h-3.5 w-52 bg-surface-100 rounded-lg mb-6" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-surface-100 p-5 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-surface-200 shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-24 bg-surface-200 rounded-lg" />
              <div className="h-3 w-16 bg-surface-100 rounded-lg" />
              <div className="h-8 w-full bg-surface-200 rounded-xl" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Skeleton: admin bookings tab ──────────────────────────────────────────────
export function BookingsTableSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {/* Desktop table */}
      <div className="hidden md:block card overflow-hidden">
        <div className="grid grid-cols-6 gap-4 px-4 py-3 bg-surface-50 border-b border-surface-100">
          {[100, 80, 90, 70, 80, 60].map((w, i) => (
            <div key={i} className="h-3 bg-surface-200 rounded-lg" style={{ width: w }} />
          ))}
        </div>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="grid grid-cols-6 gap-4 px-4 py-3.5 border-b border-surface-50">
            <div className="space-y-1.5">
              <div className="h-3.5 w-24 bg-surface-200 rounded-lg" />
              <div className="h-3 w-32 bg-surface-100 rounded-lg" />
            </div>
            <div className="h-3.5 w-20 bg-surface-200 rounded-lg self-center" />
            <div className="h-3.5 w-24 bg-surface-200 rounded-lg self-center" />
            <div className="h-3.5 w-12 bg-surface-200 rounded-lg self-center" />
            <div className="h-5 w-20 bg-surface-200 rounded-full self-center" />
            <div className="h-7 w-16 bg-surface-100 rounded-lg self-center" />
          </div>
        ))}
      </div>
      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="card p-4 border-l-4 border-l-surface-200 space-y-2">
            <div className="flex justify-between">
              <div className="h-4 w-28 bg-surface-200 rounded-lg" />
              <div className="h-4 w-12 bg-surface-200 rounded-lg" />
            </div>
            <div className="h-3 w-20 bg-surface-100 rounded-lg" />
            <div className="h-3 w-32 bg-surface-100 rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Skeleton: review cards ────────────────────────────────────────────────────
export function ReviewsSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="card p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-surface-200 shrink-0" />
            <div className="space-y-1.5 flex-1">
              <div className="h-3.5 w-28 bg-surface-200 rounded-lg" />
              <div className="flex gap-1">
                {[1,2,3,4,5].map(s => <div key={s} className="w-3.5 h-3.5 bg-surface-200 rounded" />)}
              </div>
            </div>
            <div className="h-3 w-14 bg-surface-100 rounded-lg" />
          </div>
          <div className="h-3 w-full bg-surface-100 rounded-lg" />
          <div className="h-3 w-4/5 bg-surface-100 rounded-lg" />
        </div>
      ))}
    </div>
  );
}

// ── Skeleton: staff dashboard ─────────────────────────────────────────────────
export function StaffDashboardSkeleton() {
  return (
    <div className="min-h-screen bg-surface-50 animate-pulse">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="h-7 w-48 bg-surface-200 rounded-lg" />
          <div className="h-4 w-32 bg-surface-100 rounded-lg" />
        </div>
        {/* Tabs */}
        <div className="flex gap-4 border-b border-surface-200 pb-px">
          {[72, 80, 88].map((w, i) => (
            <div key={i} className="h-3.5 bg-surface-200 rounded-lg" style={{ width: w }} />
          ))}
        </div>
        {/* Cards */}
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card p-4 border-l-4 border-l-surface-200 space-y-2">
              <div className="flex justify-between">
                <div className="h-4 w-28 bg-surface-200 rounded-lg" />
                <div className="h-4 w-12 bg-surface-200 rounded-lg" />
              </div>
              <div className="h-3 w-20 bg-surface-100 rounded-lg" />
              <div className="h-3 w-36 bg-surface-100 rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── TenantUnavailable ────────────────────────────────────────────────────────
// Shown on the PUBLIC storefront/booking page when a tenant's billing is
// locked (trial ended / subscription cancelled). Deliberately generic/neutral
// wording — a customer doesn't need to know why, just that it's not available
// right now. Never a broken-looking error; always this clean, branded page.
export function TenantUnavailable({ businessName }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-surface-50 px-4 text-center">
      <div className="text-5xl mb-4">🚧</div>
      <h2 className="font-semibold text-2xl text-ink-900 mb-2">
        {businessName ? `${businessName} isn't taking bookings right now` : "This page is temporarily unavailable"}
      </h2>
      <p className="text-ink-400 text-sm font-sans mb-6 max-w-sm">
        Please check back soon.
      </p>
      <a href="https://bookyara.com" className="btn-secondary">
        ← Back to Bookyara
      </a>
    </div>
  );
}
